import { SkillTypeDto } from '@/api/generated';
import { TFunction } from 'i18next';

export interface RatingScale {
  labels: RatingScaleLabel[];
  max: number;
  min: number;
}

export interface RatingScaleLabel {
  label: string;
  // Shown on mobile, where the full label does not fit
  shortLabel: string;
  value: number;
}

const PREFIX = 'skillsAssessmentCollectedPage';

const standardLabel = (
  t: TFunction,
  level: 'advanced' | 'basic' | 'good' | 'noKnowledge',
  value: number,
): RatingScaleLabel => ({
  label: t(`${PREFIX}.standardRatingLabels.${level}`),
  shortLabel: t(`${PREFIX}.standardRatingShortLabels.${level}`),
  value,
});

// Language levels are short enough for mobile as they are
const languageLabel = (
  t: TFunction,
  level: 'a1a2' | 'b1b2' | 'c1c2' | 'native',
  value: number,
): RatingScaleLabel => {
  const label = t(`${PREFIX}.languageRatingLabels.${level}`);
  return { label, shortLabel: label, value };
};

export const createStandardScale = (t: TFunction): RatingScale => ({
  labels: [
    standardLabel(t, 'noKnowledge', 1),
    standardLabel(t, 'basic', 2),
    standardLabel(t, 'good', 3),
    standardLabel(t, 'advanced', 4),
  ],
  max: 4,
  min: 1,
});

export const createLanguageScale = (t: TFunction): RatingScale => ({
  labels: [
    standardLabel(t, 'noKnowledge', 0),
    languageLabel(t, 'a1a2', 1),
    languageLabel(t, 'b1b2', 2),
    languageLabel(t, 'c1c2', 3),
    languageLabel(t, 'native', 4),
  ],
  max: 4,
  min: 0,
});

export const getScaleForSkillType = (type: SkillTypeDto, t: TFunction): RatingScale =>
  type === SkillTypeDto.Language ? createLanguageScale(t) : createStandardScale(t);
